import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FIELD_TYPES } from '../../../utility/constants';
import Radio from './Radio';
import { hasInputControl, MaybeInputControl } from '../InputControl';
import SelectableOptions from '../SelectableOptions';
import { applyRef, optionProp, selectedValueProp, variantProp } from '../props';
import Select from '../Select';
import Validation, { useValidation } from '../Validation';

const RadioGroup = React.forwardRef((props, ref) => {
  const {
    onChange,
    options,
    value: valueProp,
    variant,
    error,
    disabled,
    className,
    required,
  } = props;
  const { t } = useTranslation();

  const validationRef = useRef();
  const [, validationProps] = useValidation(validationRef, props);
  const value =
    typeof valueProp === 'string'
      ? options.find((opt) => opt.value === valueProp) || null
      : valueProp;

  const mergeRef = useCallback(
    (node) => {
      applyRef(ref, node);
      applyRef(validationRef, node);
    },
    [ref]
  );

  if (variant === 'underline' && options.length >= 5) {
    return (
      <Select
        placeholder={FIELD_TYPES.Radio.localizedPlaceholder(t)}
        {...props}
        value={value}
      />
    );
  }
  return (
    <MaybeInputControl {...props}>
      <SelectableOptions
        ref={mergeRef}
        className={!hasInputControl(props) && className}
      >
        {options.map((opt) => {
          const checked = Boolean(value && value.value === opt.value);
          return (
            <div key={opt.value}>
              <Radio
                label={opt.label}
                value={opt.value}
                checked={checked}
                error={error}
                disabled={disabled}
                variant={variant}
                onClick={(ev) => {
                  if (onChange && checked && !required) {
                    ev.currentTarget.blur();
                    onChange(null, ev);
                  }
                }}
                onChange={(_checked, ev) => {
                  if (onChange) onChange(opt, ev);
                }}
              />
            </div>
          );
        })}
        <Validation {...validationProps} />
      </SelectableOptions>
    </MaybeInputControl>
  );
});

export default RadioGroup;

RadioGroup.displayName = 'RadioGroup';
RadioGroup.propTypes = {
  value: selectedValueProp,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(optionProp),
  variant: variantProp,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
};
RadioGroup.defaultProps = {
  value: null,
  onChange: null,
  options: [],
  variant: null,
  error: null,
  disabled: null,
};
